import React, { useState, useEffect, useMemo } from "react";
import { Ticket, Sparkles, Globe, Check } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";

const UNLIMITED = new Set(["lifetime_free", "owner_unlimited"]);

export default function Settings() {
    const { company, user } = useAuth();
    const [code, setCode] = useState("");
    const [busy, setBusy] = useState(false);
    const [redeemedPlan, setRedeemedPlan] = useState(company?.plan);
    const alreadyUnlimited = UNLIMITED.has(redeemedPlan);

    // Sprint Q-1 · language preference state
    const [languages, setLanguages] = useState([]);
    const [currentLang, setCurrentLang] = useState("en");
    const [langQuery, setLangQuery] = useState("");
    const [langOpen, setLangOpen] = useState(false);
    const [langBusy, setLangBusy] = useState(false);

    useEffect(() => {
        api.get("/languages").then((r) => setLanguages(r.data.languages || [])).catch(() => {});
        api.get("/users/me/preferences")
            .then((r) => setCurrentLang(r.data?.preferences?.preferred_language || "en"))
            .catch(() => {});
    }, []);

    const currentLangObj = useMemo(
        () => languages.find((l) => l.code === currentLang) || { code: "en", english_name: "English", native_name: "English" },
        [languages, currentLang],
    );

    const filtered = useMemo(() => {
        const q = langQuery.trim().toLowerCase();
        if (!q) return languages;
        return languages.filter(
            (l) => l.code.toLowerCase().includes(q) || l.english_name.toLowerCase().includes(q) || l.native_name.toLowerCase().includes(q),
        );
    }, [languages, langQuery]);

    const chooseLang = async (l) => {
        setLangBusy(true);
        try {
            const { data } = await api.patch("/users/me/preferences", { preferred_language: l.code });
            setCurrentLang(data.preferences.preferred_language);
            setLangOpen(false);
            setLangQuery("");
            toast.success(`Language updated. Workforce Members will now respond in ${l.native_name}.`);
        } catch (err) {
            toast.error(err?.response?.data?.detail?.message || "Could not update language.");
        } finally {
            setLangBusy(false);
        }
    };

    const redeem = async (e) => {
        e.preventDefault();
        if (!code.trim()) return;
        setBusy(true);
        try {
            const { data } = await api.post("/coupons/redeem", { code: code.trim() });
            if (data.already_unlimited) {
                toast.success("You're already on an unlimited plan.");
            } else {
                toast.success(`Welcome, VIP. You're on the ${data.plan.replace("_", " ")} plan.`);
                setRedeemedPlan(data.plan);
                setCode("");
                setTimeout(() => window.location.reload(), 800);
            }
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Invalid VIP code");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="h-full overflow-y-auto wfos-scrollbar p-8">
            <div className="max-w-2xl">
                <div className="wfos-label">Settings</div>
                <h1 className="mt-2 text-3xl font-medium tracking-tight text-gray-900">Workspace settings</h1>
                <p className="mt-1 text-sm text-gray-500">{company?.name} · {user?.full_name}</p>

                {/* Sprint Q-1 · Language picker */}
                <section className="mt-8 wfos-card p-6" data-testid="language-picker">
                    <div className="flex items-center gap-2">
                        <Globe className="w-4 h-4 text-[#16a34a]" strokeWidth={1.75} />
                        <div className="text-[15px] font-semibold text-gray-900">Workforce Member language</div>
                    </div>
                    <p className="mt-1 text-sm text-gray-500">
                        Workforce Members and Atlas respond in your preferred language. The Nexus One UI stays in English. Legal outputs keep their English disclaimers regardless.
                    </p>

                    <div className="mt-4 relative">
                        <button
                            type="button"
                            onClick={() => setLangOpen((o) => !o)}
                            disabled={langBusy}
                            className="w-full flex items-center justify-between rounded-lg border border-[#e8e8e3] bg-white px-3 h-10 text-sm hover:border-[#16a34a] disabled:opacity-60"
                            data-testid="language-picker-toggle"
                        >
                            <span className="text-gray-900">
                                <b>{currentLangObj.native_name}</b>
                                <span className="text-gray-400 ml-2">({currentLangObj.english_name})</span>
                            </span>
                            <span className="text-xs text-gray-400">Change</span>
                        </button>

                        {langOpen && (
                            <div
                                className="absolute z-20 mt-2 w-full rounded-lg border border-[#e8e8e3] bg-white shadow-xl max-h-96 overflow-hidden flex flex-col"
                                data-testid="language-picker-dropdown"
                            >
                                <input
                                    autoFocus
                                    value={langQuery}
                                    onChange={(e) => setLangQuery(e.target.value)}
                                    placeholder="Search 100+ languages…"
                                    className="w-full px-3 h-10 text-sm border-b border-[#e8e8e3] focus:outline-none"
                                    data-testid="language-picker-search"
                                />
                                <div className="flex-1 overflow-y-auto wfos-scrollbar">
                                    {filtered.length === 0 && (
                                        <div className="px-3 py-4 text-sm text-gray-400">No match.</div>
                                    )}
                                    {filtered.map((l) => (
                                        <button
                                            key={l.code}
                                            type="button"
                                            onClick={() => chooseLang(l)}
                                            className="w-full flex items-center justify-between px-3 py-2 text-left text-sm hover:bg-gray-50"
                                            data-testid={`language-option-${l.code}`}
                                        >
                                            <span>
                                                <b>{l.native_name}</b>
                                                <span className="text-gray-400 ml-2">({l.english_name})</span>
                                            </span>
                                            <span className="flex items-center gap-2">
                                                {l.popular && (
                                                    <span className="text-[10px] text-[#16a34a] uppercase tracking-wide">· Popular</span>
                                                )}
                                                {l.code === currentLang && (
                                                    <Check className="w-4 h-4 text-[#16a34a]" strokeWidth={2} />
                                                )}
                                            </span>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </section>

                <section className="mt-6 wfos-card p-6">
                    <div className="flex items-center gap-2">
                        <Ticket className="w-4 h-4 text-[#16a34a]" strokeWidth={1.75} />
                        <div className="text-[15px] font-semibold text-gray-900">Redeem a VIP code</div>
                    </div>
                    <p className="mt-1 text-sm text-gray-500">
                        A valid VIP code upgrades your workspace to the <b>Lifetime Free</b> plan — unlimited AI
                        employees, all skill packs free, no billing prompts, ever.
                    </p>

                    {alreadyUnlimited ? (
                        <div className="mt-4 rounded-lg bg-[#dcfce7] border border-[#bbf7d0] px-3 py-2 text-sm text-[#166534] inline-flex items-center gap-2">
                            <Sparkles className="w-4 h-4" strokeWidth={2} />
                            You're already on <b className="capitalize">{redeemedPlan.replace("_", " ")}</b>.
                        </div>
                    ) : (
                        <form onSubmit={redeem} className="mt-4 flex flex-col sm:flex-row gap-2">
                            <input
                                value={code}
                                onChange={(e) => setCode(e.target.value.toUpperCase())}
                                placeholder="WFOS-LIFETIME-XXXXXXXX"
                                data-testid="settings-vip-input"
                                className="flex-1 h-10 rounded-lg border border-[#e8e8e3] bg-white px-3 text-sm font-mono focus:outline-none focus:border-[#16a34a] focus:ring-2 focus:ring-[#16a34a]/20"
                            />
                            <button
                                type="submit"
                                disabled={busy || !code.trim()}
                                data-testid="settings-vip-submit"
                                className="inline-flex items-center justify-center rounded-lg bg-[#16a34a] px-4 h-10 text-sm font-medium text-white hover:bg-[#15803d] disabled:bg-gray-200 disabled:text-gray-400"
                            >
                                {busy ? "Redeeming…" : "Redeem"}
                            </button>
                        </form>
                    )}
                </section>

                <section className="mt-6 wfos-card p-6">
                    <div className="wfos-label mb-1">Current plan</div>
                    <div className="text-2xl font-medium tracking-tight text-gray-900 capitalize">
                        {(redeemedPlan || "starter").replace("_", " ")}
                    </div>
                </section>
            </div>
        </div>
    );
}
